import { useEffect, useRef, useState } from 'react';
import { Button, Field, Input, Label, Select, Spinner, Text } from '@fluentui/react-components';
import { Dismiss16Regular } from '@fluentui/react-icons';
import type { AccessibleProject, AdHocRuleInput, FormResponses, Project, QcTemplate, Severity } from '@qcbot/shared';
import { fetchMyProjects, fetchProjectQcTemplates } from '../api';
import { getDocumentType } from '../officeApi';
import { TemplateRulesDialog } from './TemplateRulesDialog';
import { NewProjectDialog } from './NewProjectDialog';
import { NewTemplateDialog } from './NewTemplateDialog';

/** Templates saved before Word support carry no `documentTypes` and are PowerPoint-only. */
function supportsThisFile(template: QcTemplate): boolean {
  return (template.documentTypes ?? ['pptx']).includes(getDocumentType());
}

/** Keeps the judgment prompt bounded — same cap enforced again server-side (reviews/routes.ts). */
const MAX_AD_HOC_RULES = 10;
const SEVERITIES: Severity[] = ['Critical', 'High', 'Medium', 'Low', 'Info'];

export interface ProjectTemplateSelection {
  projectId?: string;
  qcTemplateId?: string;
  formResponses?: FormResponses;
  adHocRules?: AdHocRuleInput[];
}

interface Props {
  onContinue: (selection: ProjectTemplateSelection) => void;
  /** True while the parent is deciding whether a form step is needed / starting the run. */
  busy?: boolean;
  /** Restores a previous pick (e.g. the user hit "Back" from the next step) instead of
   * defaulting to the first project / that project's default template. Only consulted on the
   * initial load of projects/templates — once the user changes a dropdown themselves, normal
   * default-picking behavior resumes for anything they change after that. */
  initialSelection?: ProjectTemplateSelection;
}

/** A project's label disambiguates projects from someone else's workspace (shared via a
 * project/template collaborator grant) from the signed-in user's own. */
function projectLabel(project: AccessibleProject, ownUserId: string | undefined): string {
  return project.ownerUserId === ownUserId ? project.name : `${project.name} — shared by ${project.workspaceOwnerDisplayName}`;
}

/**
 * First step of a review run: pick a Project (from everything the signed-in user can access —
 * their own plus anything shared with them), then a QC Template within it (the `isDefault` one
 * is pre-selected). That template's own form fields (if any) are collected on the next step
 * (ProjectContextForm), not here.
 */
export function ProjectTemplateForm({ onContinue, initialSelection, busy = false }: Props) {
  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  const [projectId, setProjectId] = useState<string>('');
  const [templates, setTemplates] = useState<QcTemplate[]>([]);
  const [otherTypeTemplateCount, setOtherTypeTemplateCount] = useState(0);
  const [templateId, setTemplateId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
  const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);
  const [adHocRules, setAdHocRules] = useState<AdHocRuleInput[]>([]);
  const [newRuleTitle, setNewRuleTitle] = useState('');
  const [newRuleSeverity, setNewRuleSeverity] = useState<Severity | ''>('');
  const [showAdHocRules, setShowAdHocRules] = useState(false);

  // Captured once at mount — `initialSelection` itself never changes identity mid-component-life
  // in practice, but reading through a ref keeps this one-shot restore from re-triggering if it did.
  const initialSelectionRef = useRef(initialSelection);
  const restoredTemplateRef = useRef(false);

  useEffect(() => {
    fetchMyProjects()
      .then((rows) => {
        setProjects(rows);
        const wantedProjectId = initialSelectionRef.current?.projectId;
        const preferred = wantedProjectId && rows.some((p) => p.id === wantedProjectId) ? wantedProjectId : rows[0]?.id;
        if (preferred) setProjectId(preferred);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!projectId) {
      setTemplates([]);
      setTemplateId('');
      return;
    }
    setLoadingTemplates(true);
    const visibleTemplateIds = projects.find((p) => p.id === projectId)?.visibleTemplateIds;
    fetchProjectQcTemplates(projectId)
      .then((rows) => {
        const accessible = visibleTemplateIds ? rows.filter((t) => visibleTemplateIds.includes(t.id)) : rows;
        // Only templates enabled for the open file's type (Word vs PowerPoint) can be used.
        const visible = accessible.filter(supportsThisFile);
        setOtherTypeTemplateCount(accessible.length - visible.length);
        setTemplates(visible);
        const wantedTemplateId = !restoredTemplateRef.current ? initialSelectionRef.current?.qcTemplateId : undefined;
        restoredTemplateRef.current = true;
        const preferred = (wantedTemplateId ? visible.find((t) => t.id === wantedTemplateId) : undefined) ?? visible.find((t) => t.isDefault) ?? visible[0];
        setTemplateId(preferred?.id ?? '');
      })
      .finally(() => setLoadingTemplates(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const canContinue = !loadingTemplates;
  const ownUserId = projects.find((p) => p.accessLevel === 'owner')?.ownerUserId;

  function addAdHocRule() {
    const title = newRuleTitle.trim();
    if (!title || adHocRules.length >= MAX_AD_HOC_RULES) return;
    setAdHocRules((prev) => [...prev, { title, severityGuidance: newRuleSeverity || undefined }]);
    setNewRuleTitle('');
    setNewRuleSeverity('');
  }

  function removeAdHocRule(index: number) {
    setAdHocRules((prev) => prev.filter((_, i) => i !== index));
  }

  /** Re-fetches so the picked-from list (and anything else `fetchMyProjects` derives, e.g.
   * visibleTemplateIds) reflects the brand-new row, rather than hand-rolling an AccessibleProject
   * shape from the plainer `Project` the create endpoint returns. */
  async function handleProjectCreated(project: Project) {
    const rows = await fetchMyProjects();
    setProjects(rows);
    setProjectId(project.id);
  }

  async function handleTemplateCreated(template: QcTemplate) {
    const rows = await fetchProjectQcTemplates(projectId);
    setTemplates(rows.filter(supportsThisFile));
    setTemplateId(template.id);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Spinner size="tiny" /> Loading projects…
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Text size={200} style={{ color: '#666' }}>
          No projects yet — create one to set up QC templates and AI-generated rules, or continue without one (uses the default QC framework).
        </Text>
        <Button appearance="primary" onClick={() => setNewProjectDialogOpen(true)}>
          New project
        </Button>
        <Button appearance="secondary" disabled={busy} onClick={() => onContinue({})}>
          Continue without a project
        </Button>
        <NewProjectDialog open={newProjectDialogOpen} onClose={() => setNewProjectDialogOpen(false)} onCreated={(p) => void handleProjectCreated(p)} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Field label="Project" required>
        <Select value={projectId} onChange={(_, d) => setProjectId(d.value)}>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {projectLabel(project, ownUserId)}
            </option>
          ))}
        </Select>
      </Field>
      <Button
        appearance="transparent"
        size="small"
        style={{ alignSelf: 'flex-start', padding: 0, minWidth: 0, fontSize: 12, textDecoration: 'underline' }}
        onClick={() => setNewProjectDialogOpen(true)}
      >
        + New project
      </Button>

      {loadingTemplates ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size="tiny" /> Loading QC templates…
        </div>
      ) : (
        <>
          {templates.length === 0 && otherTypeTemplateCount > 0 && (
            <Text size={200} style={{ color: '#666' }}>
              None of this project's QC templates are enabled for {getDocumentType() === 'docx' ? 'Word documents' : 'PowerPoint decks'}. Ask the project owner to enable one, or create a new template below.
            </Text>
          )}
          {templates.length > 0 && (
            <Field label="QC Template" required>
              <Select value={templateId} onChange={(_, d) => setTemplateId(d.value)}>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </>
      )}

      {templateId && (
        <Button
          appearance="transparent"
          size="small"
          style={{ alignSelf: 'flex-start', padding: 0, minWidth: 0, fontSize: 12, textDecoration: 'underline' }}
          onClick={() => setRulesDialogOpen(true)}
        >
          View this template's rules
        </Button>
      )}

      <Button
        appearance="transparent"
        size="small"
        style={{ alignSelf: 'flex-start', padding: 0, minWidth: 0, fontSize: 12, textDecoration: 'underline' }}
        onClick={() => setNewTemplateDialogOpen(true)}
      >
        + New QC template{templates.length === 0 ? ' (with AI-generated rules)' : ''}
      </Button>

      {/* V1-HIDDEN: custom rules for this run. Uncomment to restore.
      {showAdHocRules ? (
        <div style={{ background: '#f5f5f5', borderRadius: 6, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Label weight="semibold" size="small">
              Add custom rules for this run (optional)
            </Label>
            <Button appearance="transparent" size="small" style={{ padding: 0, minWidth: 0, fontSize: 12 }} onClick={() => setShowAdHocRules(false)}>
              Hide
            </Button>
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Checked alongside the template's own rules for this run only — not saved to the template.
          </div>
          {adHocRules.map((rule, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <span style={{ flex: 1 }}>{rule.title}</span>
              {rule.severityGuidance && <span style={{ fontSize: 11, color: '#666' }}>{rule.severityGuidance}</span>}
              <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} onClick={() => removeAdHocRule(index)} />
            </div>
          ))}
          {adHocRules.length < MAX_AD_HOC_RULES ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Field label="Rule">
                <Input
                  value={newRuleTitle}
                  onChange={(_, d) => setNewRuleTitle(d.value)}
                  placeholder='e.g. "Verify every chart cites its data source"'
                />
              </Field>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                <Field label="Severity" style={{ flex: 1 }}>
                  <Select value={newRuleSeverity} onChange={(_, d) => setNewRuleSeverity(d.value as Severity | '')}>
                    <option value="">Default (Medium)</option>
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Button appearance="secondary" disabled={!newRuleTitle.trim()} onClick={addAdHocRule}>
                  Add
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#666' }}>Limit of {MAX_AD_HOC_RULES} custom rules per run reached.</div>
          )}
        </div>
      ) : (
        <Button
          appearance="transparent"
          size="small"
          style={{ alignSelf: 'flex-start', padding: 0, minWidth: 0, fontSize: 12, textDecoration: 'underline' }}
          onClick={() => setShowAdHocRules(true)}
        >
          + Add custom rules for this run{adHocRules.length > 0 ? ` (${adHocRules.length} added)` : ' (optional)'}
        </Button>
      )}
      */}

      <Button
        appearance="primary"
        disabled={!canContinue || busy}
        onClick={() =>
          onContinue({
            projectId: projectId || undefined,
            qcTemplateId: templateId || undefined,
            adHocRules: adHocRules.length > 0 ? adHocRules : undefined,
          })
        }
      >
        {busy ? 'Starting…' : 'Continue'}
      </Button>
      {templateId && <TemplateRulesDialog open={rulesDialogOpen} onClose={() => setRulesDialogOpen(false)} templateId={templateId} />}
      <NewProjectDialog open={newProjectDialogOpen} onClose={() => setNewProjectDialogOpen(false)} onCreated={(p) => void handleProjectCreated(p)} />
      <NewTemplateDialog
        open={newTemplateDialogOpen}
        onClose={() => setNewTemplateDialogOpen(false)}
        onCreated={(t) => void handleTemplateCreated(t)}
        projectId={projectId}
        isFirstTemplate={templates.length === 0}
      />
    </div>
  );
}
